"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { API_URL } from "../config"
import ResultsChart from "../components/calculator/ResultsChart"
import "./ETrefHistory.css"

import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import "jspdf-autotable"

const ETrefHistory = () => {
  const [calculations, setCalculations] = useState([])
  const [selectedCalculation, setSelectedCalculation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchCalculations = async () => {
      try {
        setLoading(true)
        const res = await axios.get(`${API_URL}/api/etref/history`)
        setCalculations(res.data)
        setLoading(false)
      } catch (err) {
        setError("Failed to load calculation history")
        setLoading(false)
        console.error(err)
      }
    }

    fetchCalculations()
  }, [])

  const handleSelectCalculation = async (calculationId) => {
    if (selectedCalculation && selectedCalculation._id === calculationId) {
      setSelectedCalculation(null)
      return
    }

    try {
      setLoading(true)
      const res = await axios.get(`${API_URL}/api/etref/calculations/${calculationId}`)
      setSelectedCalculation(res.data)
      setLoading(false)
    } catch (err) {
      setError("Failed to load calculation details")
      setLoading(false)
      console.error(err)
    }
  }

  const getLocationDisplay = (item) => {
    const location = item?.location
  
    if (location?.district?.name && location?.country?.name) {
      return `${location.district.name}, ${location.country.name}`
    }
  
    if (location?.coordinates) {
      const { lat, lng } = location.coordinates
      return `${lat?.toFixed(2)}, ${lng?.toFixed(2)}`
    }
  
    return "Unknown"
  }
  

  const exportHistoryToExcel = () => {
    const worksheetData = calculations.map((calc) => ({
      Date: new Date(calc.date).toLocaleDateString(),
      Location: getLocationDisplay(calc),
      Models: calc.models.length,
      Created: new Date(calc.createdAt).toLocaleDateString(),
    }))
    const worksheet = XLSX.utils.json_to_sheet(worksheetData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "WATref History")
    XLSX.writeFile(workbook, "WATref_Calculation_History.xlsx")
  }

  const exportCalculationDetailsToExcel = () => {
    if (!selectedCalculation) return

    const metaSheetData = [
      ["Date", new Date(selectedCalculation.date).toLocaleDateString()],
      ["Location", getLocationDisplay(selectedCalculation)],
      ["Number of Models", selectedCalculation.models.length],
    ]
    const metaSheet = XLSX.utils.aoa_to_sheet(metaSheetData)

    const modelsData = selectedCalculation.models.map((model) => ({
      Model: model.name,
      "Value (mm/day)": model.value.toFixed(2),
    }))
    const modelsSheet = XLSX.utils.json_to_sheet(modelsData)

    const weather = selectedCalculation.weatherData
    const weatherData = [
      ["Max Temp (\u00B0C)", weather.tempMax],
      ["Min Temp (\u00B0C)", weather.tempMin],
      ["Humidity (%)", weather.humidity],
      ["Wind Speed (m/s)", weather.windSpeed],
      ["Solar Radiation (MJ/m\u00B2)", weather.solarRadiation],
      ["Precipitation (mm)", weather.precipitation],
    ]
    const weatherSheet = XLSX.utils.aoa_to_sheet(weatherData)

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, metaSheet, "Meta Info")
    XLSX.utils.book_append_sheet(workbook, modelsSheet, "Models")
    XLSX.utils.book_append_sheet(workbook, weatherSheet, "Weather Data")

    XLSX.writeFile(workbook, `WATref_Calculation_Details_${selectedCalculation._id}.xlsx`)
  }

  const exportCalculationDetailsToPDF = () => {
    if (!selectedCalculation) return

    const doc = new jsPDF()
    doc.setFontSize(16)
    doc.text("WATref Calculation Details", 14, 20)

    doc.setFontSize(12)
    doc.text("Meta Information:", 14, 30)
    const metaInfo = [
      ["Date", new Date(selectedCalculation.date).toLocaleDateString()],
      ["Location", getLocationDisplay(selectedCalculation)],
      ["Number of Models", selectedCalculation.models.length.toString()],
    ]
    let startY = 36
    metaInfo.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, 14, startY)
      startY += 6
    })

    doc.text("Models Results:", 14, startY + 6)
    const modelColumns = ["Model", "Value (mm/day)"]
    const modelRows = selectedCalculation.models.map((m) => [m.name, m.value.toFixed(2)])

    doc.autoTable({
      startY: startY + 10,
      head: [modelColumns],
      body: modelRows,
      styles: { fillColor: [220, 240, 255] },
      headStyles: { fillColor: [0, 102, 204], textColor: 255 },
    })

    const weatherStartY = doc.lastAutoTable.finalY + 10
    doc.text("Weather Data:", 14, weatherStartY)
    const weatherData = [
      ["Max Temp (\u00B0C)", selectedCalculation.weatherData.tempMax],
      ["Min Temp (\u00B0C)", selectedCalculation.weatherData.tempMin],
      ["Humidity (%)", selectedCalculation.weatherData.humidity],
      ["Wind Speed (m/s)", selectedCalculation.weatherData.windSpeed],
      ["Solar Radiation (MJ/m\u00B2)", selectedCalculation.weatherData.solarRadiation],
      ["Precipitation (mm)", selectedCalculation.weatherData.precipitation],
    ]

    doc.autoTable({
      startY: weatherStartY + 6,
      head: [["Parameter", "Value"]],
      body: weatherData,
      styles: { fillColor: [255, 255, 224] },
      headStyles: { fillColor: [255, 153, 0], textColor: 0 },
    })

    doc.save(`WATref_Calculation_Details_${selectedCalculation._id}.pdf`)
  }

  return (
    <div className="etref-history-container">
      <div className="etref-history-header">
        <h1>WATref Calculation History</h1>
        <p className="etref-history-subtitle">View and analyze your previous WATref calculations</p>
      </div>

      {error && (
        <div className="alert-error etref-alert">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      <div className="etref-history-content">
        <div className="calculations-list-card">
          <div className="card-header">
            <h2>Previous Calculations</h2>
            <button
              className="card-action-button export"
              onClick={exportHistoryToExcel}
              title="Export history as Excel"
              style={{ float: "right", marginTop: "-32px" }}
            >
              <i className="fas fa-file-export"></i> Export Excel
            </button>
          </div>
          <div className="card-body">
            {loading && calculations.length === 0 ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading calculations...</p>
              </div>
            ) : calculations.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-calculator empty-icon"></i>
                <p>No calculation history found</p>
              </div>
            ) : (
              <div className="calculations-table-container">
                <table className="calculations-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Location</th>
                      <th>Models</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculations.map((calc) => (
                      <tr
                        key={calc._id}
                        className={selectedCalculation && selectedCalculation._id === calc._id ? "selected" : ""}
                      >
                        <td>{new Date(calc.date).toLocaleDateString()}</td>
                        <td>{getLocationDisplay(calc)}</td>
                        <td>{calc.models.length} models</td>
                        <td>{new Date(calc.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="table-action-button view"
                              onClick={() => handleSelectCalculation(calc._id)}
                              title="View details"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="table-action-button export"
                              onClick={() => {
                                setSelectedCalculation(calc)
                                setTimeout(() => exportCalculationDetailsToExcel(), 100)
                              }}
                              title="Export this calculation as Excel"
                            >
                              <i className="fas fa-file-export"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {selectedCalculation && (
          <div className="calculation-details-card">
            <div className="card-header">
              <h2>Calculation Details</h2>
              <div className="card-actions">
                <button
                  className="card-action-button"
                  onClick={exportCalculationDetailsToExcel}
                  title="Export details to Excel"
                >
                  <i className="fas fa-file-export"></i> Export
                </button>
                <button
                  className="card-action-button"
                  onClick={exportCalculationDetailsToPDF}
                  title="Print / Export to PDF"
                >
                  <i className="fas fa-print"></i> Print
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="calculation-meta">
                <div className="meta-item">
                  <span className="meta-label">Date:</span>
                  <span className="meta-value">{new Date(selectedCalculation.date).toLocaleDateString()}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Location:</span>
                  <span className="meta-value">{getLocationDisplay(selectedCalculation)}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Models:</span>
                  <span className="meta-value">{selectedCalculation.models.length}</span>
                </div>
              </div>

              <div className="calculation-results">
                <h3>Results</h3>
                <div className="results-table-container">
                  <table className="results-table">
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th>Value (mm/day)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCalculation.models.map((model) => (
                        <tr key={model.name}>
                          <td>{model.name}</td>
                          <td>{model.value.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* <div className="weather-data-summary">
                <h3>Weather Data</h3>
                <div className="weather-data-grid">
                  <div className="weather-data-item">
                    <span className="weather-label">Max Temp:</span>
                    <span className="weather-value">{selectedCalculation.weatherData.tempMax}°C</span>
                  </div>
                  <div className="weather-data-item">
                    <span className="weather-label">Min Temp:</span>
                    <span className="weather-value">{selectedCalculation.weatherData.tempMin}°C</span>
                  </div>
                  <div className="weather-data-item">
                    <span className="weather-label">Humidity:</span>
                    <span className="weather-value">{selectedCalculation.weatherData.humidity}%</span>
                  </div>
                  <div className="weather-data-item">
                    <span className="weather-label">Wind Speed:</span>
                    <span className="weather-value">{selectedCalculation.weatherData.windSpeed} m/s</span>
                  </div>
                  <div className="weather-data-item">
                    <span className="weather-label">Solar Radiation:</span>
                    <span className="weather-value">{selectedCalculation.weatherData.solarRadiation} MJ/m²</span>
                  </div>
                  <div className="weather-data-item">
                    <span className="weather-label">Precipitation:</span>
                    <span className="weather-value">{selectedCalculation.weatherData.precipitation} mm</span>
                  </div>
                </div>
              </div> */}

              <div className="results-chart-container">
                <h3>Results Comparison</h3>
                <ResultsChart
                  results={{
                    models: selectedCalculation.models.map((model) => ({ id: model.name, name: model.name })),
                    data: [
                      {
                        date: selectedCalculation.date,
                        etref: selectedCalculation.models.reduce((acc, model) => {
                          acc[model.name] = model.value
                          return acc
                        }, {}),
                      },
                    ],
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ETrefHistory
